import { Stack, Button, Text, Shelf, Banner } from '@chaser/components';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import FileUpload from '../../../../components/FileUpload';
import ImportService from '../../../../services/import-service';
import MappingField, { processUsedColumnsForType } from './Field';
import styles from './Mapping.module.scss';
import { toast } from '../../../../components/ToastContainer';
import { ReadOnlyContext } from './readOnlyContext';
import config from '../../../../config';

const parseMapping = mapping => {
  if (mapping && Array.isArray(mapping.fields)) {
    return Object.fromEntries(
      mapping.fields.map(({ id, value }) => {
        if (value.__typename === 'CSVMappingFieldValueLastName') {
          return [id, { ...value, columnId: value.optionalColumnId, optionalColumnId: undefined }];
        }
        return [id, value];
      }),
    );
  }

  return {};
};

const useAvailableColumns = data => {
  const allColumns = useMemo(() => {
    if (!data || data.length === 0) return {};

    const keys = data[0];

    const obj = Object.fromEntries(Object.values(keys).map(key => [key, new Set()]));

    data.slice(1).forEach(row => {
      Object.entries(row).forEach(([key, value]) => {
        if (obj[keys[key]]) {
          obj[keys[key]].add(value);
        }
      });
    });

    return Object.fromEntries(Object.entries(obj).map(([key, value]) => [key, Array.from(value)]));
  }, [data]);

  const [usedColumns, setUsedColumns] = useState(() => ({}));

  const availableColumns = useMemo(() => {
    const usedValues = Object.values(usedColumns);
    return Object.fromEntries(
      Object.entries(allColumns).map(([columnHeader, values]) => {
        const column = {
          label: columnHeader,
          values,
          availableValues: [...values],
          available: true,
        };

        for (const usedValuesObject of usedValues) {
          if (!usedValuesObject) continue;

          const usedValues = usedValuesObject[columnHeader];

          if (usedValues === undefined) {
            continue;
          } else if (usedValues === true) {
            column.available = false;
          } else if (Array.isArray(usedValues)) {
            column.available = false;

            for (const usedValue of usedValues) {
              if (column.availableValues.includes(usedValue)) {
                column.availableValues.splice(column.availableValues.indexOf(usedValue), 1);
              }
            }
          } else {
            throw new Error(`Unknown type of used value: ${typeof usedValues}`);
          }
        }

        return [columnHeader, column];
      }),
    );
  }, [allColumns, usedColumns]);

  return [availableColumns, setUsedColumns];
};

const Mapping = ({ shortCode, type, mapping, maxFileSize, onCompleted }) => {
  const [csvData, setCSVData] = useState(null);

  const onAllFilesUploaded = useCallback(
    async (successes, failures) => {
      if (failures.length > 0) {
        const err = new Error('Upload failed, please try again.');
        err.expose = true;
        throw err;
      } else {
        const { prefix, name } = successes[0];
        const result = await ImportService.readCSV(shortCode, prefix, name);

        setCSVData({ prefix, name, data: result });
      }
    },
    [shortCode],
  );

  const [localMapping, setLocalMapping] = useState(() => parseMapping(mapping));
  const [lastSavedMapping, setLastSavedMapping] = useState(localMapping);

  useEffect(() => {
    type.fields.forEach(field => {
      const value = localMapping[field.id];
      processUsedColumnsForType(field, value, setUsedColumns);
    });
    // we want to run this only once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setValue = useCallback(cb => {
    setLocalMapping(cb);
  }, []);

  const [availableColumns, setUsedColumns] = useAvailableColumns(csvData?.data);

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  const showMapping = Boolean(csvData || mapping);

  const saveMapping = useCallback(async () => {
    setIsSaving(true);
    setError(null);
    try {
      const mappingForRequest = Object.entries(localMapping)
        .filter(([, value]) => value)
        .map(([id, value]) => ({
          id,
          value: {
            [value.__typename]: {
              ...value,
              __typename: undefined,
              ...(value.__typename === 'CSVMappingFieldValueTags' && {
                mapping: {
                  ...(value?.mapping?.__typename && {
                    [value?.mapping?.__typename]: {
                      ...value?.mapping,
                      __typename: undefined,
                    },
                  }),
                },
              }),
            },
          },
        }));

      await ImportService.saveCSVMappingConfiguration(
        shortCode,
        {
          type: { id: type.id, fields: mappingForRequest },
        },
        csvData.prefix,
        csvData.name,
      );
      toast.success('Mapping saved successfully.');
      setLastSavedMapping(localMapping);
      onCompleted();
    } catch (err) {
      const { code } = err;
      if (code === 'CSV_MAPPING_NOT_MATCHING_SPECIFICATION' || code === 'CSV_VALIDATION_ERROR') {
        const extra =
          code === 'CSV_VALIDATION_ERROR'
            ? ', you can either update mapping or upload updated test file'
            : '';
        setError({
          message: `Please correct following problems and try again${extra}:`,
          list: err.meta.errors,
        });
        toast.error('Failed to save mapping. Please correct listed issues and try again.');
      } else {
        toast.error(
          'Unexpected error occured. Please try again. If problem persists, contact support.',
        );
      }
    } finally {
      setIsSaving(false);
    }
  }, [shortCode, localMapping, csvData, type.id, onCompleted]);

  const warningBanner = !csvData && (
    <Banner
      showCloseButton={false}
      backgroundColor="Light Neutral 2"
      borderColor="transparent"
      icon="alert-square"
    >
      Upload a file above to modify mapping.
    </Banner>
  );

  const lastSavedMappingStringified = useMemo(() => JSON.stringify(lastSavedMapping), [
    lastSavedMapping,
  ]);

  // lodash isEqual is not working here, probably due to use of undefined in the mapping
  const hasChanges = useMemo(() => JSON.stringify(localMapping) !== lastSavedMappingStringified, [
    localMapping,
    lastSavedMappingStringified,
  ]);

  const errorInfo = useMemo(
    () =>
      error && (
        <>
          <div>
            <strong>{error.message}</strong>
          </div>
          {error.list && (
            <ul>
              {error.list.map(err => (
                <li>{err}</li>
              ))}
            </ul>
          )}
        </>
      ),
    [error],
  );

  return (
    <ReadOnlyContext.Provider value={!csvData}>
      <Stack gap="medium">
        <FileUpload
          accept={{ 'text/csv': ['.csv'] }}
          maxSize={maxFileSize}
          getUploadInfo={ImportService.getCSVUploadLinks.bind(null, shortCode)}
          onAllFilesUploaded={onAllFilesUploaded}
        />

        {showMapping && (
          <>
            {warningBanner}

            <table>
              <thead>
                <tr className={styles.tableHeadRow}>
                  <th className={styles.tableHeadCell}>Accepted Chaser fields</th>
                  <th className={styles.tableHeadCell}>Mandatory</th>
                  <th className={styles.tableHeadCell}>Description</th>
                  <th className={styles.tableHeadCell}>Values in CSV</th>
                </tr>
              </thead>

              <tbody>
                {type.fields.map(field => (
                  <MappingField
                    key={field.id}
                    field={field}
                    setValue={setValue}
                    value={localMapping[field.id]}
                    availableColumns={availableColumns}
                    setUsedColumns={setUsedColumns}
                  />
                ))}
              </tbody>
            </table>

            <Shelf justifyContent={csvData ? 'space-between' : 'flex-end'} alignItems="flex-start">
              <Text color="Red">{errorInfo}</Text>

              {!csvData && <div style={{ width: 'fit-content' }}>{warningBanner}</div>}

              <Button
                onClick={saveMapping}
                loading={isSaving}
                success={!hasChanges}
                successMessage="Saved"
                disabled={!csvData}
              >
                Save
              </Button>
            </Shelf>

            {import.meta.env.VITE_ENV === 'development' && (
              <pre>{JSON.stringify(localMapping, null, 2)}</pre>
            )}
          </>
        )}
      </Stack>
    </ReadOnlyContext.Provider>
  );
};

export default Mapping;
